import React, { useEffect } from "react";
import { Typography } from "@mui/material";
import { useField } from "formik";

import RpDatePicker from "@/components/muiCustom/date/RpDatePicker";
import RRDDirectMailToolTips from "../RRDDirectMailToolTips";
import { rrdDirectMailCampaignHelper } from "@/features/campaigns/direct-mail/rrd/utils/rrdDirectMailHelper";

const DatePicker = RpDatePicker as React.ElementType;

type RRDDirectFrontCouponExpInputProps = {
  name?: string;
  mailingDateFieldName?: string;
  differentCouponExpiryFieldName?: string;
  containerClassName?: string;
  expiryDateConstraints?: Record<string, any>;
  minCouponDurationDays?: number | string | null;
  couponExpirationSetBy?: string;
  fixedCouponDurationDays?: number | string | null;
};

function RRDDirectFrontCouponExpInput({
  name = "front_coupon_expiry_date",
  mailingDateFieldName = "mailing_date",
  differentCouponExpiryFieldName = "different_coupon_expiry",
  containerClassName = "",
  expiryDateConstraints,
  minCouponDurationDays,
  couponExpirationSetBy,
  fixedCouponDurationDays,
}: RRDDirectFrontCouponExpInputProps) {
  const [couponExpiryField, , couponExpiryHelpers] = useField(name);

  const [mailingDateField] = useField(mailingDateFieldName);

  const [differentCouponExpiryField] = useField(differentCouponExpiryFieldName);
  const isBrandSetCouponExpiration =
    couponExpirationSetBy?.toLowerCase() === "brand" &&
    !!fixedCouponDurationDays;

  const calculatedBrandExpiryDate =
    rrdDirectMailCampaignHelper.getBrandSetCouponExpiryDate(
      mailingDateField.value,
      fixedCouponDurationDays
    );

  useEffect(() => {
    if (isBrandSetCouponExpiration) {
      if (calculatedBrandExpiryDate !== couponExpiryField.value) {
        couponExpiryHelpers.setValue(calculatedBrandExpiryDate);
      }
      return;
    }

    const nextValue =
      rrdDirectMailCampaignHelper.getConstrainedCouponExpiryValue(
        couponExpiryField.value,
        expiryDateConstraints
      );

    if (nextValue !== couponExpiryField.value) {
      couponExpiryHelpers.setValue(nextValue);
    }
  }, [
    calculatedBrandExpiryDate,
    couponExpiryField.value,
    couponExpiryHelpers,
    expiryDateConstraints,
    isBrandSetCouponExpiration,
  ]);

  const handleOnCouponExpiryChange = (
    d: { format: (_format: string) => string } | null
  ) => {
    const value = d ? d.format("YYYY-MM-DD") : "";
    couponExpiryHelpers.setValue(value);
  };

  const couponExpiryLabel = differentCouponExpiryField.value
    ? "Front Coupon Expiration Date"
    : "Coupon Expiration Date";

  const couponExpiryValue = isBrandSetCouponExpiration
    ? calculatedBrandExpiryDate || couponExpiryField.value
    : couponExpiryField.value;
  const minCouponExpiryDate = isBrandSetCouponExpiration
    ? undefined
    : expiryDateConstraints?.minCouponExpiryDate;
  const maxCouponExpiryDate = isBrandSetCouponExpiration
    ? undefined
    : expiryDateConstraints?.maxCouponExpiryDate;

  return (
    <>
      <Typography
        variant="body2"
        component="span"
        gutterBottom
        fontWeight={600}
        className="mb-2"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          lineHeight: 1.35,
          width: "fit-content",
          maxWidth: "100%",
          whiteSpace: "nowrap",
        }}
      >
        <span>{couponExpiryLabel}</span>
        <RRDDirectMailToolTips
          type="coupon_expiration_date"
          minCouponDurationDays={minCouponDurationDays}
          minCouponExpirationDate={expiryDateConstraints?.minCouponExpiryDate}
          maxCouponExpirationDate={expiryDateConstraints?.maxCouponExpiryDate}
        />
      </Typography>
      <DatePicker
        containerClassName={containerClassName}
        name={name}
        value={couponExpiryValue}
        onChange={handleOnCouponExpiryChange}
        borderColor="var(--primary-base)"
        hideCalendarIcon
        highlightWeekDaysOnly
        minDate={minCouponExpiryDate}
        maxDate={maxCouponExpiryDate}
        disabled={isBrandSetCouponExpiration || !mailingDateField.value}
        format="MMM DD, YYYY"
        className="rrd-coupon-expiry-input"
        smallSize={true}
      />
    </>
  );
}

export default RRDDirectFrontCouponExpInput;
